// Shared by every place that renders a Builder3 raw-HTML site inside a
// srcdoc iframe (SiteView.vue's /site/:id, PreviewSiteView.vue's
// /preview/:token) — same fixed, human-written scripts, same trust model:
// never generated per-page, never written by the model.

// The iframe these run in deliberately omits allow-same-origin (unlike
// Builder3's editor preview, which needs it for click-to-edit DOM access).
// allow-scripts + NOT allow-same-origin gives the document an opaque/null
// origin — real JS runs (routers, menus, form handlers, whatever the page
// needs), but it cannot read quotastic.ca's cookies/localStorage or reach
// window.parent, since it isn't considered the same origin at all. That's
// what lets us safely run someone else's/an AI's script here.

// srcdoc documents inherit the *parent* page's URL as their base URL for
// resolving relative links (e.g. an <a href="#about">'s .href resolves to
// the real quotastic.ca/site/<id>#about, not about:srcdoc#about). Any
// pushState/replaceState call built from that resolved URL then throws
// SecurityError (opaque origin vs. the real origin can never match), which
// aborts the caller mid-function — breaking hash-router templates that
// update the DOM *after* the history call. We can't fix arbitrary pasted/
// generated site scripts, so patch history to swallow that specific error
// before the site's own scripts run.
const HISTORY_SHIM =
  '<script>(function(){var h=window.history;["pushState","replaceState"].forEach(function(k){' +
  'var orig=h[k];h[k]=function(){try{return orig.apply(h,arguments)}catch(e){' +
  'if(e&&e.name==="SecurityError")return;throw e}}})})();</' +
  'script>'

// Same base-URL quirk as the history shim above, but for plain in-page nav
// links: <a href="#about"> resolves against the *parent* page's real URL,
// not this opaque srcdoc document — so it doesn't match "the current
// document" and the browser tries to navigate the iframe to that URL
// instead of scrolling. Intercept same-page hash clicks and scroll manually
// so nav/anchor links work as authored.
const ANCHOR_SHIM =
  '<script>(function(){document.addEventListener("click",function(e){' +
  'var a=e.target.closest&&e.target.closest(\'a[href^="#"]\');' +
  'if(!a)return;var id=a.getAttribute("href").slice(1);if(!id)return;' +
  'var el=document.getElementById(id);if(!el)return;' +
  'e.preventDefault();el.scrollIntoView({behavior:"smooth",block:"start"});' +
  '},true)})();</' +
  'script>'

// Progressive enhancement for the checkbox-hack mobile nav (see the
// hamburger-menu rule in staticSiteGeneration.js's systemPrompt): the
// checkbox/label/<nav> markup already works with zero JS (it has to — the
// Builder3 editor's preview iframe can't run scripts at all), but a
// checkbox alone can't do things that need something to *see* a click
// happen: closing the menu after tapping a nav link, or after tapping
// outside it. It targets the mandated "nav-toggle" id/"for" attribute
// rather than any specific class name, since that's the one part of the
// markup convention that's a hard "must" rather than an example.
const NAV_TOGGLE_SHIM =
  '<script>(function(){' +
  'function navFor(cb){var w=cb.parentElement;return(w&&w.querySelector("nav"))||document.querySelector("nav");}' +
  'document.addEventListener("click",function(e){' +
  'var cb=document.getElementById("nav-toggle");' +
  'if(!cb||!cb.checked)return;' +
  'var label=document.querySelector(\'label[for="nav-toggle"]\');' +
  'if(e.target===cb||(label&&label.contains(e.target)))return;' +
  'var nav=navFor(cb);' +
  'if(nav&&nav.contains(e.target)&&!e.target.closest("a"))return;' +
  'cb.checked=false;' +
  '});' +
  'document.addEventListener("change",function(e){' +
  'if(e.target.id!=="nav-toggle")return;' +
  'var label=document.querySelector(\'label[for="nav-toggle"]\');' +
  'if(label)label.setAttribute("aria-expanded",String(e.target.checked));' +
  '});' +
  '})();</' +
  'script>'

// The contact form's redirect_to/#form-success:target mechanism (see the
// contact-form rule in staticSiteGeneration.js) assumes a 303 redirect lands
// back on "the same document" so :target can match. That's true when the
// page is served as a real top-level document (Cloudflare Pages), but not
// here: this is srcdoc content with no address of its own, so the redirect
// actually reloads the *entire outer Vue app* one level deeper inside the
// iframe, which then renders a *fresh* nested srcdoc iframe with no fragment
// of its own — the confirmation banner's :target rule never sees it. Submit
// the form via fetch instead and flip the CSS with a same-document
// location.hash change (:target responds to that identically to a real
// navigation, but it never leaves this document, so nesting never happens).
// redirect_to is stripped from the payload so the backend takes its
// existing plain-JSON branch (see handleFormSubmission in server.js)
// instead of attempting the 303 this is deliberately replacing.
const FORM_SHIM =
  '<script>(function(){' +
  'document.addEventListener("submit",function(e){' +
  'var form=e.target;' +
  'if(!form||!form.matches||!form.matches(\'form[action*="/api/form-submission"]\'))return;' +
  'e.preventDefault();' +
  'var data=new FormData(form);' +
  'data.delete("redirect_to");' +
  'fetch(form.getAttribute("action"),{method:"POST",body:new URLSearchParams(data)})' +
  '.then(function(r){return r.json().catch(function(){return{};}).then(function(d){' +
  'return Boolean(r.ok&&d&&d.ok);' +
  '});})' +
  '.then(function(ok){' +
  'if(ok)form.reset();' +
  'location.hash=ok?"form-success":"form-error";' +
  '})' +
  '.catch(function(){location.hash="form-error";});' +
  '},true)})();</' +
  'script>'

const INJECTED_SHIMS = HISTORY_SHIM + ANCHOR_SHIM + NAV_TOGGLE_SHIM + FORM_SHIM

export function injectRawSiteShims(html) {
  if (typeof html !== 'string' || !html) return ''

  return /<head[^>]*>/i.test(html)
    ? html.replace(/<head[^>]*>/i, (match) => `${match}${INJECTED_SHIMS}`)
    : INJECTED_SHIMS + html
}
